/**
 * @format
 * @flow
 */

import { database, firestore } from './firebase';

type SessionCallbacks = {
  onCartUpdate?: ?(cart: Object, sessionState: string) => void,
  onSessionStateChange?: ?(string) => void,
  onError?: ?(Error) => void,
  onDocumentDeleted?: ?() => void
};

type CartData = {
  products: Array<Object>,
  total: number,
  totalHT: number,
  totalVat: number,
  totalProducts: number,
  subtotal: number,
  discount: number,
  comment: string,
  coupon: ?Object,
  restoId: string,
  restoName: string,
  restoPostal: string,
  restoInformations: Object,
  canOrder: boolean,
  canOrderDelivery: boolean,
  orderType: number,
  table: string,
  qrId: string,
  createdAt: number,
  lastUpdated: number
};

type SharedSession = {
  id: string,
  restoId: string,
  tableId: string,
  qrId: string,
  cart: CartData,
  sessionState: string,
  createdAt: number,
  lastUpdated: number
};

/**
 * Firebase Realtime Database service for shared cart management
 * Replaces Redux persist with real-time Firebase storage
 */
class SharedCartService {
  currentSession: ?SharedSession;
  unsubscribeSession: ?Function;
  sessionCallbacks: SessionCallbacks;
  isInitialized: boolean;
  dbRef: ?Object;
  sessionParams: ?{restoId: string, tableId: string, qrId: string};

  constructor() {
    this.currentSession = null;
    this.unsubscribeSession = null;
    this.sessionCallbacks = {
      onCartUpdate: null,
      onSessionStateChange: null,
      onError: null,
      onDocumentDeleted: null
    };
    this.isInitialized = false;
    this.dbRef = null;
    this.sessionParams = null;
  }



  /**
   * Initialize a shared cart session using Firebase Realtime Database
   * @param {string} restoId - Restaurant ID
   * @param {string} tableId - Table ID
   * @param {string} qrId - QR code ID
   * @param {Object} callbacks - Event callbacks
   * @returns {Promise<string>} Session ID
   */
  async initializeSession(restoId: string, tableId: string, qrId: string, callbacks: SessionCallbacks = {}): Promise<string> {
    try {
      // Store parameters for potential reinitialization
      this.sessionParams = { restoId, tableId, qrId };
      this.sessionCallbacks = { ...this.sessionCallbacks, ...callbacks };
      const sessionId = `${restoId}_${tableId}`;

      // Reference to Firebase Realtime Database
      this.dbRef = database().ref(`sharedCarts/${sessionId}`);

      // Check if session exists
      const snapshot = await this.dbRef.once('value');
      const sessionData = snapshot.val();

      if (!sessionData) {
        // Create new session with empty cart
        const newSession = {
          id: sessionId,
          restoId,
          tableId,
          qrId,
          cart: {
            products: [],
            total: 0,
            totalHT: 0,
            totalVat: 0,
            totalProducts: 0,
            subtotal: 0,
            discount: 0,
            comment: '',
            coupon: null,
            restoId,
            restoName: '',
            restoPostal: '',
            restoInformations: {},
            canOrder: false,
            canOrderDelivery: false,
            orderType: 3, // Table ordering
            table: '',
            qrId,
            createdAt: Date.now(),
            lastUpdated: Date.now()
          },
          sessionState: 'active',
          createdAt: Date.now(),
          lastUpdated: Date.now()
        };

        await this.dbRef.set(newSession);
        this.currentSession = newSession;
      } else {
        // Join existing session
        await this.dbRef.child('lastUpdated').set(Date.now());

        this.currentSession = { ...sessionData, id: sessionId };

        // Validate the cart structure we're joining
        if (!sessionData.cart?.products || !Array.isArray(sessionData.cart.products)) {
          console.warn('⚠️ Joining session with invalid products array. Fixing...');
          // Fix the products array if it's missing or invalid
          this.currentSession.cart.products = [];
        }
      }

      // Set up real-time listeners
      this.setupSessionListener();
      this.isInitialized = true;

      return sessionId;
    } catch (error) {
      console.error('Error initializing shared session:', error);
      if (this.sessionCallbacks.onError) {
        this.sessionCallbacks.onError(error);
      }
      throw error;
    }
  }

  /**
   * Validate session data structure
   * @param {Object} data - Session data from Firebase
   * @returns {boolean} Whether data is valid
   */
  validateSessionData(data) {
    // Basic structure validation
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Cart validation - must exist and be object
    if (!data.cart || typeof data.cart !== 'object') {
      return false;
    }

    // Products validation - if exists, must be array (but can be missing for new sessions)
    if (data.cart.products !== undefined && !Array.isArray(data.cart.products)) {
      return false;
    }


    // All validations passed
    return true;
  }

  /**
   * Set up real-time listener for session changes
   */
  setupSessionListener() {
    if (!this.dbRef) return;

    this.unsubscribeSession = this.dbRef.on('value', (snapshot) => {
      const data = snapshot.val();

      if (data && this.validateSessionData(data)) {
        const prevSession = this.currentSession;

        this.currentSession = { ...data, id: data.id };

        // Special handling for completed sessions
        if (data.sessionState === 'completed') {

          // Ensure cart is empty for completed sessions
          if (data.cart && data.cart.products && data.cart.products.length > 0) {
            console.warn('⚠️ Completed session has non-empty cart, clearing...');
            data.cart.products = [];
            data.cart.total = 0;
            data.cart.totalHT = 0;
            data.cart.totalVat = 0;
            data.cart.totalProducts = 0;
            data.cart.subtotal = 0;
          }

          // Fire session state change first for completed sessions
          if (this.sessionCallbacks.onSessionStateChange &&
              prevSession?.sessionState !== 'completed') {
            this.sessionCallbacks.onSessionStateChange('completed');
          }
        }

        // Notify about cart updates with session state
        if (this.sessionCallbacks.onCartUpdate && data.cart &&
            JSON.stringify(prevSession?.cart) !== JSON.stringify(data.cart)) {
          this.sessionCallbacks.onCartUpdate(data.cart, data.sessionState || 'active');
        }

        // Notify about session state changes (for non-completed states)
        if (data.sessionState !== 'completed' &&
            this.sessionCallbacks.onSessionStateChange &&
            prevSession?.sessionState !== data.sessionState) {
          this.sessionCallbacks.onSessionStateChange(data.sessionState);
        }
      } else if (data) {
        console.warn('⚠️ Invalid session data received from Firebase:', data);
      } else {
        // Document deleted or never existed
        if (this.currentSession !== null && this.sessionParams) {
          // Document was deleted - existing session lost
          // Store current params and callbacks before cleanup
          const params = this.sessionParams;
          const callbacks = this.sessionCallbacks;

          // Clean up current state but preserve params and callbacks
          this.currentSession = null;
          this.isInitialized = false;
          // Don't null out sessionParams and sessionCallbacks - we need them for reinitialization

          // Don't await here to avoid blocking the listener
          // Remove current listener first to avoid duplicate listeners
          if (this.unsubscribeSession && this.dbRef) {
            this.dbRef.off('value', this.unsubscribeSession);
            this.unsubscribeSession = null;
          }

          // Reinitialize session
          this.initializeSession(params.restoId, params.tableId, params.qrId, callbacks)
            .then(() => {
              // Notify component that reinitialization is complete
              if (callbacks.onDocumentDeleted) {
                callbacks.onDocumentDeleted();
              }
            })
            .catch(error => {
              console.error('❌ Failed to reinitialize session after document deletion:', error);
              if (callbacks.onError) {
                callbacks.onError(error);
              }
            });
        } else if (this.currentSession !== null) {
          // Session was deleted but we don't have params to reinitialize
          if (this.sessionCallbacks.onDocumentDeleted) {
            this.sessionCallbacks.onDocumentDeleted();
          }
        }
      }
    }, (error) => {
      console.error('❌ Session listener error:', error);
      if (this.sessionCallbacks.onError) {
        this.sessionCallbacks.onError(error);
      }
    });
  }

  /**
   * Update cart data in Firebase Realtime Database
   * @param {Object} cartData - Updated cart data
   * @returns {Promise<void>}
   */
  async updateCart(cartData: Object): Promise<void> {
    if (!this.isInitialized || !this.dbRef) {
      throw new Error('Session not initialized');
    }

    try {
      const updates = {
        'cart': {
          ...cartData,
          lastUpdated: Date.now()
        },
        'lastUpdated': Date.now()
      };

      await this.dbRef.update(updates);
    } catch (error) {
      console.error('Error updating cart:', error);
      if (this.sessionCallbacks.onError) {
        this.sessionCallbacks.onError(error);
      }
      throw error;
    }
  }

  /**
   * Calculate cart totals from products array
   * @param {Array<Object>} products - Products array
   * @returns {Object} Cart totals
   */
  calculateCartTotals(products: Array<Object>): Object {
    if (!products || products.length === 0) {
      return {
        total: 0,
        totalHT: 0,
        totalVat: 0,
        totalProducts: 0,
        subtotal: 0
      };
    }

    let total = 0;
    let totalHT = 0;
    let totalVat = 0;
    let totalProducts = 0;
    let subtotal = 0;

    products.forEach(product => {
      if (product && typeof product === 'object') {
        total += (product.total || 0);
        totalHT += (product.totalHT || 0);
        totalVat += (product.totalVat || 0);
        totalProducts += (product.quantity || 0);
        subtotal += (product.subtotal || 0);
      }
    });

    return {
      total,
      totalHT,
      totalVat,
      totalProducts,
      subtotal
    };
  }

  /**
   * Add a product to the shared cart
   * @param {Object} product - Product to add
   * @returns {Promise<void>}
   */
  async addProduct(product: Object): Promise<void> {
    if (!this.currentSession) {
      console.warn('⚠️ Cannot add product: no active session');
      return;
    }

    // Robust defensive programming: ensure cart and products exist
    const currentCart = this.currentSession.cart || {};

    // Defensive programming: ensure products is always an array
    const currentProducts = Array.isArray(currentCart.products) ? currentCart.products : [];

    const updatedProducts = [...currentProducts, product];

    // Calculate new totals based on updated products
    const totals = this.calculateCartTotals(updatedProducts);

    const updatedCart = {
      ...currentCart,
      products: updatedProducts,
      ...totals,
      lastUpdated: Date.now()
    };

    await this.updateCart(updatedCart);
  }

  /**
   * Update a product in the shared cart
   * @param {number} index - Product index
   * @param {Object} updatedProduct - Updated product
   * @returns {Promise<void>}
   */
  async updateProduct(index: number, updatedProduct: Object): Promise<void> {
    if (!this.currentSession) {
      console.warn('⚠️ Cannot update product: no active session');
      return;
    }

    // Robust defensive programming: ensure cart and products exist
    const currentCart = this.currentSession.cart || {};

    // Defensive programming: ensure products is always an array
    const currentProducts = Array.isArray(currentCart.products) ? currentCart.products : [];

    const updatedProducts = [...currentProducts];
    updatedProducts[index] = updatedProduct;

    // Calculate new totals based on updated products
    const totals = this.calculateCartTotals(updatedProducts);

    const updatedCart = {
      ...currentCart,
      products: updatedProducts,
      ...totals,
      lastUpdated: Date.now()
    };

    await this.updateCart(updatedCart);
  }

  /**
   * Remove a product from the shared cart
   * @param {number} index - Product index
   * @returns {Promise<void>}
   */
  async removeProduct(index: number): Promise<void> {
    if (!this.currentSession) {
      console.warn('⚠️ Cannot remove product: no active session');
      return;
    }

    // Robust defensive programming: ensure cart and products exist
    const currentCart = this.currentSession.cart || {};

    // Defensive programming: ensure products is always an array
    const currentProducts = Array.isArray(currentCart.products) ? currentCart.products : [];

    const updatedProducts = [...currentProducts];
    updatedProducts.splice(index, 1);

    // Calculate new totals based on updated products
    const totals = this.calculateCartTotals(updatedProducts);

    const updatedCart = {
      ...currentCart,
      products: updatedProducts,
      ...totals,
      lastUpdated: Date.now()
    };

    await this.updateCart(updatedCart);
  }

  /**
   * Update cart totals
   * @param {Object} totals - Cart totals
   * @returns {Promise<void>}
   */
  async updateCartTotals(totals: Object): Promise<void> {
    if (!this.currentSession) return;

    const currentCart = this.currentSession.cart || {};
    const updatedCart = {
      ...currentCart,
      ...totals,
      lastUpdated: Date.now()
    };

    await this.updateCart(updatedCart);
  }

  /**
   * Update cart comment
   * @param {string} comment - Cart comment
   * @returns {Promise<void>}
   */
  async updateComment(comment: string): Promise<void> {
    if (!this.currentSession) return;

    const currentCart = this.currentSession.cart || {};
    const updatedCart = {
      ...currentCart,
      comment,
      lastUpdated: Date.now()
    };

    await this.updateCart(updatedCart);
  }

  /**
   * Lock session for checkout (prevent further modifications)
   * @deprecated Use acquireLock() for atomic locking
   * @returns {Promise<void>}
   */
  async lockSession(): Promise<void> {
    if (!this.dbRef) return;

    try {
      const updates = {
        'sessionState': 'locked',
        'lockedAt': Date.now(),
        'lastUpdated': Date.now()
      };

      await this.dbRef.update(updates);
    } catch (error) {
      console.error('Error locking session:', error);
      throw error;
    }
  }

  /**
   * Atomically acquire lock for session validation
   * Prevents race conditions when multiple users try to validate simultaneously
   * @returns {Promise<boolean>} True if lock acquired, false if already locked
   */
  async acquireLock(): Promise<boolean> {
    if (!this.dbRef) return false;

    try {
      let lockAcquired = false;

      await this.dbRef.transaction((currentData) => {
        if (!currentData) {
          // No data exists - shouldn't happen in normal flow
          console.warn('No session data during lock acquisition');
          return currentData;
        }

        // Check if already locked or completed
        if (currentData.sessionState === 'locked' ||
            currentData.sessionState === 'completed') {
          // Already locked or completed - abort transaction
          lockAcquired = false;
          return; // Abort transaction by returning undefined
        }

        // Acquire lock atomically
        lockAcquired = true;
        currentData.sessionState = 'locked';
        currentData.lockedAt = Date.now();
        currentData.lockedBy = this.sessionParams?.qrId || 'unknown';
        currentData.lastUpdated = Date.now();
        return currentData;
      });

      return lockAcquired;
    } catch (error) {
      console.error('❌ Error acquiring lock:', error);
      return false;
    }
  }

  /**
   * Release lock for session validation
   * Should be called after validation is complete or if validation fails
   * @returns {Promise<boolean>} True if lock released successfully
   */
  async releaseLock(): Promise<boolean> {
    if (!this.dbRef) return false;

    try {
      let lockReleased = false;

      await this.dbRef.transaction((currentData) => {
        if (!currentData) {
          console.warn('No session data during lock release');
          return currentData;
        }

        // Only release if currently locked by this user
        if (currentData.sessionState !== 'locked') {
          lockReleased = false;
          return currentData; // No change needed
        }

        // Verify lock ownership (optional safety check)
        const currentUserQrId = this.sessionParams?.qrId || 'unknown';
        if (currentData.lockedBy && currentData.lockedBy !== currentUserQrId) {
          console.warn('⚠️ Attempting to release lock owned by different user');
          lockReleased = false;
          return currentData; // Don't release someone else's lock
        }

        // Release lock atomically
        lockReleased = true;
        currentData.sessionState = 'active';
        delete currentData.lockedAt;
        delete currentData.lockedBy;
        currentData.lastUpdated = Date.now();
        return currentData;
      });

      return lockReleased;
    } catch (error) {
      console.error('❌ Error releasing lock:', error);
      return false;
    }
  }

  /**
   * Check for stale locks and clear them if they've timed out
   * Should be called before attempting to acquire a lock
   * @param {number} timeoutMs - Lock timeout in milliseconds (default: 5 minutes)
   * @returns {Promise<boolean>} True if a stale lock was cleared
   */
  async checkAndClearStaleLock(timeoutMs: number = 300000): Promise<boolean> {
    if (!this.dbRef) return false;

    try {
      let staleLockCleared = false;

      await this.dbRef.transaction((currentData) => {
        if (!currentData || currentData.sessionState !== 'locked') {
          // No lock to check
          return currentData;
        }

        const now = Date.now();
        const lockAge = now - (currentData.lockedAt || 0);

        if (lockAge > timeoutMs) {
          // Lock has timed out - clear it
          console.warn(`⏰ Clearing stale lock (age: ${Math.round(lockAge / 1000)}s, timeout: ${Math.round(timeoutMs / 1000)}s)`);
          staleLockCleared = true;
          currentData.sessionState = 'active';
          delete currentData.lockedAt;
          delete currentData.lockedBy;
          currentData.lastUpdated = now;
          return currentData;
        }

        // Lock is still valid
        return currentData;
      });

      return staleLockCleared;
    } catch (error) {
      console.error('❌ Error checking/clearing stale lock:', error);
      return false;
    }
  }

  /**
   * Complete session and place order
   * @param {Object} orderData - Order data
   * @returns {Promise<void>}
   */
  async completeSession(orderData: Object): Promise<void> {
    if (!this.currentSession || !this.dbRef) return;

    try {
      // Verify lock ownership before proceeding
      if (this.currentSession.sessionState !== 'locked') {
        throw new Error('Cannot complete session: session is not locked');
      }

      const currentUserQrId = this.sessionParams?.qrId || 'unknown';
      if (this.currentSession.lockedBy !== currentUserQrId) {
        throw new Error('Cannot complete session: session is locked by another user');
      }

      // Update QRCode document with order (using Firestore for orders)
      const qrRef = firestore()
        .collection(`Restaurant/${this.currentSession.restoId}/QRCode`)
        .doc(this.currentSession.qrId);

      await qrRef.update({
        createdAt: orderData.createdAt,
        order: firestore.FieldValue.arrayUnion(orderData),
        status: 2,
        confirmedAt: orderData.confirmedAt
      });

      // Clear cart and mark session as completed in Realtime Database
      const updates = {
        'cart': {
          products: [],
          total: 0,
          totalHT: 0,
          totalVat: 0,
          totalProducts: 0,
          subtotal: 0,
          discount: 0,
          comment: '',
          coupon: null,
          restoId: this.currentSession.restoId,
          restoName: this.currentSession.cart?.restoName || '',
          restoPostal: this.currentSession.cart?.restoPostal || '',
          restoInformations: this.currentSession.cart?.restoInformations || {},
          canOrder: this.currentSession.cart?.canOrder || false,
          canOrderDelivery: this.currentSession.cart?.canOrderDelivery || false,
          orderType: this.currentSession.cart?.orderType || 3,
          table: this.currentSession.cart?.table || '',
          qrId: this.currentSession.qrId,
          createdAt: Date.now(),
          lastUpdated: Date.now()
        },
        'sessionState': 'completed',
        'completedAt': Date.now(),
        'orderData': orderData,
        'lastUpdated': Date.now()
      };

      await this.dbRef.update(updates);

    } catch (error) {
      console.error('❌ Error completing session:', error);
      throw error;
    }
  }

  /**
   * Reset session after order completion - clear cart but maintain connection
   * @returns {Promise<void>}
   */
  async resetSession(): Promise<void> {
    if (!this.currentSession || !this.dbRef) return;

    try {
      // Create empty cart while preserving restaurant information
      const resetCart = {
        products: [],
        total: 0,
        totalHT: 0,
        totalVat: 0,
        totalProducts: 0,
        subtotal: 0,
        discount: 0,
        comment: '',
        coupon: null,
        restoId: this.currentSession.restoId,
        restoName: this.currentSession.cart?.restoName || '',
        restoPostal: this.currentSession.cart?.restoPostal || '',
        restoInformations: this.currentSession.cart?.restoInformations || {},
        canOrder: this.currentSession.cart?.canOrder || false,
        canOrderDelivery: this.currentSession.cart?.canOrderDelivery || false,
        orderType: this.currentSession.cart?.orderType || 3,
        table: this.currentSession.cart?.table || '',
        qrId: this.currentSession.qrId,
        createdAt: Date.now(),
        lastUpdated: Date.now()
      };

      // Reset session state to active with empty cart
      const updates = {
        'cart': resetCart,
        'sessionState': 'active',
        'lastUpdated': Date.now(),
        // Clear previous order data
        'completedAt': null,
        'orderData': null
      };

      await this.dbRef.update(updates);

      // Update local session data
      this.currentSession.cart = resetCart;
      this.currentSession.sessionState = 'active';

    } catch (error) {
      console.error('❌ Error resetting session:', error);
      throw error;
    }
  }

  /**
   * Leave the current session
   * @returns {Promise<void>}
   */
  async leaveSession(): Promise<void> {
    try {
      if (this.dbRef) {
        await this.dbRef.child('lastUpdated').set(Date.now());
      }
    } catch (error) {
      console.error('Error updating lastUpdated during leave:', error);
    } finally {
      // Always cleanup regardless of errors to prevent listener leaks
      this.cleanup();
    }
  }

  /**
   * Get current cart data
   * @returns {Object|null} Current cart
   */
  getCurrentCart(): ?Object {
    return this.currentSession?.cart || null;
  }

  /**
   * Get current session data
   * @returns {Object|null} Current session
   */
  getCurrentSession(): ?SharedSession {
    return this.currentSession;
  }

  /**
   * Check if currently in shared mode
   * @returns {boolean} Whether in shared mode
   */
  isInSharedMode(): boolean {
    return this.isInitialized && this.currentSession !== null;
  }


  /**
   * Clean up listeners and reset state
   */
  cleanup() {
    if (this.unsubscribeSession && this.dbRef) {
      this.dbRef.off('value', this.unsubscribeSession);
      this.unsubscribeSession = null;
    }

    this.dbRef = null;
    this.currentSession = null;
    this.isInitialized = false;
    this.sessionParams = null;
    this.sessionCallbacks = {
      onCartUpdate: null,
      onSessionStateChange: null,
      onError: null,
      onDocumentDeleted: null
    };
  }
}

// Export singleton instance
const sharedCartService = new SharedCartService();
export default sharedCartService;